import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import Loader from '../../../components/Loader/Loader';
import SurveyDetailHeader from './SurveyDetailHeader';
import SurveyDetailTabs from './SurveyDetailTabs';
import SurveyOverviewTab from './SurveyOverviewTab';
import SurveyAudienceTab from './SurveyAudienceTab';
import SurveyCompletionTab from './SurveyCompletionTab';
import SurveyAnalyticsTab from './SurveyAnalyticsTab';
import SurveyAnswersTab from './SurveyAnswersTab';
import { getSurveyById, getSurveyResults, exportSurveyCompletion, remindPendingStudents } from '../../../services/SurveyService';
import './SurveyList.css';
import './SurveyDetail.css';

const SurveyDetail = () => {
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [summary, setSummary] = useState(null);
  const [isLoader, setIsLoader] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const fetchSurvey = useCallback(async () => {
    try {
      setIsLoader(true);
      setNotFound(false);
      const [surveyResponse, resultsResponse] = await Promise.all([
        getSurveyById(id),
        getSurveyResults(id),
      ]);
      if (surveyResponse.status === 200) {
        setSurvey(surveyResponse.data.data);
      }
      if (resultsResponse.status === 200) {
        setSummary(resultsResponse.data.data);
      }
    } catch (error) {
      console.error('API Error:', error.response);
      if (error.response?.status === 404) setNotFound(true);
    } finally {
      setIsLoader(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSurvey();
  }, [fetchSurvey]);

  const handleExportCsv = async () => {
    try {
      const response = await exportSurveyCompletion(id);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `survey-${id}-completion.csv`;
      link.click();
    } catch (error) {
      console.error('API Error:', error.response);
      enqueueSnackbar('Could not export the completion report', { variant: 'error' });
    }
  };

  const handleRemindPending = async () => {
    try {
      const response = await remindPendingStudents(id);
      enqueueSnackbar(response.data.message.success, { variant: 'success' });
    } catch (error) {
      console.error('API Error:', error.response);
      const errors = error.response?.data?.message?.errors;
      enqueueSnackbar(errors?.[0] || 'Could not send the reminder', { variant: 'error' });
    }
  };

  const handleViewAnswers = (studentId) => {
    setSelectedStudentId(studentId);
    setActiveTab('answers');
  };

  if (isLoader) {
    return (
      <div className="animated survey-detail-page">
        <Loader />
      </div>
    );
  }

  if (notFound || !survey || String(survey.id) !== String(id)) {
    return (
      <div className="animated survey-detail-page">
        <div className="card survey-panel">
          <div className="survey-panel-body">Survey not found.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="animated survey-detail-page">
      <SurveyDetailHeader survey={survey} onExportCsv={handleExportCsv} onRemindPending={handleRemindPending} />
      <SurveyDetailTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'overview' && summary && (
        <SurveyOverviewTab survey={survey} summary={summary} onNarrowToStudents={() => setActiveTab('audience')} />
      )}
      {activeTab === 'audience' && (
        <SurveyAudienceTab survey={survey} onAudienceSaved={fetchSurvey} />
      )}
      {activeTab === 'completion' && (
        <SurveyCompletionTab survey={survey} onExportCsv={handleExportCsv} onViewAnswers={handleViewAnswers} />
      )}
      {activeTab === 'analytics' && <SurveyAnalyticsTab survey={survey} />}
      {activeTab === 'answers' && (
        <SurveyAnswersTab survey={survey} selectedStudentId={selectedStudentId} onSelectStudent={setSelectedStudentId} />
      )}
    </div>
  );
};

export default SurveyDetail;
